/**
 * The menu's sound — synthesized, not sampled.
 *
 * Three tiny WebAudio gestures and nothing else: a hover tick, a select click, and the heavy
 * press reserved for PLAY. Synthesis over samples on purpose — no asset to load, no network
 * round trip on the first hover, no licence, and the palette (short sine blips over a filtered
 * thump) reads as instrument feedback, which is the register the whole shell is drawn in.
 *
 * Ground rules, each load-bearing:
 *   - **The context is created lazily on the first call**, which in practice is always inside
 *     a user gesture (pointer/click handlers are the only callers), so the autoplay policy is
 *     satisfied without a resume dance. If the context still arrives suspended, one `resume()`
 *     is attempted and failure is swallowed — sound is decoration here, never load-bearing.
 *   - **`masterVolume` is read from the player's real settings on every play**, so the arena's
 *     volume slider governs the menu too, and 0 means silence — not "quiet".
 *   - **Hover ticks are mouse-only.** A `pointerenter` from a touch pointer is a tap already
 *     in flight; ticking it would double every press.
 *   - Everything is wrapped so an environment with no `AudioContext` (jsdom, ancient webviews)
 *     gets a silent menu, never a thrown error.
 */
import { loadSettings } from '../settings';

type Ctx = AudioContext;

let ctx: Ctx | null = null;
let broken = false;

function context(): Ctx | null {
  if (broken) return null;
  if (ctx) return ctx;
  try {
    const Ctor =
      typeof AudioContext !== 'undefined'
        ? AudioContext
        : (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      broken = true;
      return null;
    }
    ctx = new Ctor();
  } catch {
    broken = true;
    return null;
  }
  if (ctx.state === 'suspended') void ctx.resume().catch(() => {});
  return ctx;
}

/** Master gain for one gesture, or null when the player has volume at zero. */
function gainFor(base: number): number | null {
  let volume = 1;
  try {
    volume = loadSettings().masterVolume;
  } catch {
    /* settings unreadable → default volume */
  }
  const g = base * volume;
  return g <= 0.0005 ? null : g;
}

/** One enveloped oscillator — every gesture below is built from these. */
function blip(
  audio: Ctx,
  opts: { freq: number; freqTo?: number; type?: OscillatorType; at?: number; durMs: number; gain: number },
): void {
  const t0 = audio.currentTime + (opts.at ?? 0);
  const dur = opts.durMs / 1000;
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.type = opts.type ?? 'sine';
  osc.frequency.setValueAtTime(opts.freq, t0);
  if (opts.freqTo) osc.frequency.exponentialRampToValueAtTime(opts.freqTo, t0 + dur);
  gain.gain.setValueAtTime(0, t0);
  gain.gain.linearRampToValueAtTime(opts.gain, t0 + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(gain).connect(audio.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** Hover: one very quiet, very short high tick. */
export function menuTick(): void {
  const audio = context();
  const g = gainFor(0.035);
  if (!audio || g === null) return;
  try {
    blip(audio, { freq: 2300, durMs: 28, gain: g });
  } catch {
    broken = true;
  }
}

/** Select / confirm: a two-tone click, rising — "accepted". */
export function menuClick(): void {
  const audio = context();
  const g = gainFor(0.06);
  if (!audio || g === null) return;
  try {
    blip(audio, { freq: 1300, durMs: 42, gain: g });
    blip(audio, { freq: 1750, durMs: 55, gain: g * 0.9, at: 0.045 });
  } catch {
    broken = true;
  }
}

/** PLAY: the one heavy gesture — a low thump under a short bright edge. */
export function menuHeavy(): void {
  const audio = context();
  const g = gainFor(0.11);
  if (!audio || g === null) return;
  try {
    blip(audio, { freq: 170, freqTo: 70, type: 'triangle', durMs: 160, gain: g });
    blip(audio, { freq: 1900, durMs: 30, gain: g * 0.35 });
  } catch {
    broken = true;
  }
}

// ── Generated samples (2026-09-03) ─────────────────────────────────────────────────────────
//
// The synthesized tick/click/heavy trio stays for the tiny gestures; everything with an EVENT
// behind it — match found, a claim collected, a match starting, a panel closing, a page
// turning, gear going on — plays a real generated sample (`public/game/audio/ui/`, prompts in
// ASSETS.md). Decoded once into WebAudio buffers on first use, played through the same lazily
// created context, scaled by the same master volume. A missing file or a failed decode plays
// nothing — sound is never load-bearing.
const SAMPLES = {
  matchFound: '/game/audio/ui/match-found.mp3',
  collect: '/game/audio/ui/collect.mp3',
  start: '/game/audio/ui/start.mp3',
  close: '/game/audio/ui/close.mp3',
  transition: '/game/audio/ui/transition.mp3',
  equipGear: '/game/audio/ui/equip-gear.mp3',
  equipWeapon: '/game/audio/ui/equip-weapon.mp3',
  select: '/game/audio/ui/select.mp3',
} as const;
export type MenuSample = keyof typeof SAMPLES;

/** Per-sample base gain — an alert may shout, a page turn must not. */
const SAMPLE_GAIN: Record<MenuSample, number> = {
  matchFound: 0.5,
  collect: 0.4,
  start: 0.5,
  close: 0.22,
  transition: 0.16,
  equipGear: 0.35,
  equipWeapon: 0.4,
  select: 0.25,
};

const bufferCache = new Map<MenuSample, Promise<AudioBuffer | null>>();

function bufferFor(name: MenuSample): Promise<AudioBuffer | null> {
  let cached = bufferCache.get(name);
  if (!cached) {
    cached = (async () => {
      const audio = context();
      if (!audio || typeof fetch !== 'function') return null;
      try {
        const res = await fetch(SAMPLES[name]);
        if (!res.ok) return null;
        return await audio.decodeAudioData(await res.arrayBuffer());
      } catch {
        return null;
      }
    })();
    bufferCache.set(name, cached);
  }
  return cached;
}

/** Plays one of the generated UI samples. Fire-and-forget, silent on any failure. */
export function playSample(name: MenuSample): void {
  const audio = context();
  const g = gainFor(SAMPLE_GAIN[name]);
  if (!audio || g === null) return;
  void bufferFor(name).then((buffer) => {
    if (!buffer) return;
    try {
      const src = audio.createBufferSource();
      const gain = audio.createGain();
      src.buffer = buffer;
      gain.gain.value = g;
      src.connect(gain).connect(audio.destination);
      src.start();
    } catch {
      /* decoration */
    }
  });
}

/** Wires the standard hover/press pair onto a control. Hover ticks are mouse-only. */
export function wireMenuSfx(el: HTMLElement, press: 'click' | 'heavy' = 'click'): void {
  el.addEventListener('pointerenter', (event) => {
    if (event.pointerType === 'mouse') menuTick();
  });
  el.addEventListener('click', press === 'heavy' ? menuHeavy : menuClick);
}
